import CssBaseline from '@material-ui/core/CssBaseline';
import { ThemeProvider } from '@material-ui/core/styles';
import * as Sentry from '@sentry/react';
import withRedux from 'next-redux-wrapper';
import Head from 'next/head';
import { useRouter } from 'next/router';
import PropTypes from 'prop-types';
import React, { useEffect } from 'react';
import { createIntl, createIntlCache, RawIntlProvider } from 'react-intl';
import { Provider, useSelector } from 'react-redux';
import Favicon from '~/assets/favicon.png';
import socialMediaLogo from '~/assets/social_media_logo.jpg';
import ChowbusErrorBoundary from '~/components/ChowbusErrorBoundary.jsx';
import ReduxResponsiveDialog from '~/components/ReduxResponsiveDialog.jsx';
import { userCookie } from '~/models/UserCookie';
import * as shoppingCartActions from '~/store/legacy/actions/shoppingCartActions';
import * as userActions from '~/store/legacy/actions/userActions';
import { configureStore } from '~/store/redux.js';
import theme from '~/theme/theme';
import { mixpanel } from '~/third_party/Mixpanel.jsx';
import { enableErrorHandler, setStore } from '../api/request';
import Alert from '../components/Alert.jsx';
import { version } from '../package.json';
import * as Castle from '@castleio/castle-js';
import { AppsflyerPBA } from '~/third_party/Appsflyer';

import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";

// This is optional but highly recommended
// since it prevents memory leak
const cache = createIntlCache();
const isServer = typeof window === 'undefined';
const CHOWBUS_DESCRIPTION =
  'Chowbus is a food delivery platform providing high quality, authentic Asian food to our users with easy ordering experience and consistently high quality delivery service.';

const loadPolyfills = async () => {
  if (typeof window.IntersectionObserver === 'undefined') {
    await import('intersection-observer');
  }
};
// suppress useLayoutEffect warnings when running outside a browser
if (isServer) React.useLayoutEffect = React.useEffect;
if (!isServer) {
  Sentry.init({
    dsn: process.env.SENTRY_DSN,
    environment: process.env.CHOWBUS_ENV,
    release: `chowbus-diner-web@${version}`,
  });
  loadPolyfills();
  enableErrorHandler();
}

const initCastle = () => {
  let CASTLE_APP_ID = process.env.CASTLE_APP_ID;
  if (CASTLE_APP_ID) {
    Castle.configure(CASTLE_APP_ID);
  } else {
    console.log('==CASTLE_APP== init Failed');
  }
};

const initApp = async (store) => {
  mixpanel.track({ name: 'Open web app' });
  AppsflyerPBA.trackWebStartSession();
  setVersion();
  initCastle();
  store.dispatch(shoppingCartActions.initCart());
  // even if the user is not logged in, they might have an address cookie
  store.dispatch(userActions.initUser(userCookie));
  if (userCookie.isLoggedIn) {
    await store.dispatch(userActions.getUser());
  }
};

const setVersion = () => {
  window.APP_VERSION = process.env.APP_VERSION;
  window.BUILD_TAG = process.env.NEXT_PUBLIC_BUILD_TAG;
};

const removeServerStyles = () => {
  const jssStyles = document.querySelector('#jss-server-side');
  if (jssStyles) {
    jssStyles.parentElement.removeChild(jssStyles);
  }
};

const IntlProvider = (props) => {
  const { children } = props;
  const { locale, messages } = useSelector((state) => state.app);
  const intl = createIntl({ locale, messages }, cache);
  return <RawIntlProvider value={intl}>{children}</RawIntlProvider>;
};

const ChowbusApp = (props) => {
  const { Component, pageProps, store } = props;
  const router = useRouter();
  useEffect(removeServerStyles, []);
  useEffect(() => {
    initApp(store);
  }, []);

  return (
    <>
      <Head>
        <title>Best asian food, delivered</title>
        <meta name='viewport' content='minimum-scale=1, initial-scale=1, width=device-width' />
        <link rel='icon' type='image/x-icon' href={Favicon} />
        <link rel='canonical' href={`https://www.chowbus.com${router.asPath}`} />

        <meta
          name='viewport'
          content='width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no'
        />
        <meta charSet='utf-8' />
        <meta
          name='google-site-verification'
          content='0jiLDUpy4VWJ5KRQWIAfMO-83WMJ0Flpv22S_DFayWc'
        />
        <meta name='description' content={CHOWBUS_DESCRIPTION} />

        <meta property='og:site_name' content='chowbus' />
        <meta property='og:title' content='chowbus - Best Asian Food, Delivered' />
        <meta property='og:url' content={`https://www.chowbus.com${router.asPath}`} />
        <meta property='og:type' content='website' />
        <meta property='og:description' content={CHOWBUS_DESCRIPTION} />
        <meta property='og:image' content={socialMediaLogo} />
        <meta property='og:image:width' content='320' />
        <meta property='og:image:height' content='320' />

        <meta itemProp='name' content='Chowbus' />
        <meta itemProp='url' content={`https://www.chowbus.com${router.asPath}`} />
        <meta itemProp='description' content={CHOWBUS_DESCRIPTION} />
        <meta itemProp='thumbnailUrl' content={socialMediaLogo} />
        <link rel='image_src' href={socialMediaLogo} />
        <meta itemProp='image' content={socialMediaLogo} />

        <meta name='twitter:title' content='chowbus - Best Asian Food, Delivered' />
        <meta name='twitter:image' content={socialMediaLogo} />
        <meta name='twitter:url' content={`https://www.chowbus.com${router.asPath}`} />
        <meta name='twitter:card' content='summary' />
        <meta name='twitter:description' content={CHOWBUS_DESCRIPTION} />
        <meta name='p:domain_verify' content='9a0b8e95e415ef8c2db97abd1cca42c1' />
      </Head>

      <ThemeProvider theme={theme}>
        <ChowbusErrorBoundary>
          <CssBaseline />
          <Provider store={store}>
            <IntlProvider>
              <Component {...pageProps} />
            </IntlProvider>
            <Alert />
            <ReduxResponsiveDialog />
          </Provider>
        </ChowbusErrorBoundary>
      </ThemeProvider>
    </>
  );
};

ChowbusApp.propTypes = {
  Component: PropTypes.elementType.isRequired,
  pageProps: PropTypes.object,
};

const makeStore = (initialState) => {
  const store = configureStore(initialState);
  setStore(store);
  return store;
};

//withRedux wrapper that passes the store to the App Component
export default withRedux(makeStore, null)(ChowbusApp);
